import type { ReactiveController, ReactiveControllerHost } from "lit";

import type { Ref } from "lit/directives/ref.js";

/**
 * Generic interface for draggable items - requires only an id
 */
export interface DraggedItem {
  id: string;
}

/**
 * Drop position information
 */
export interface DropPosition {
  targetItemId: string;
  side: "left" | "right" | "top" | "bottom";
  targetElement: HTMLElement;
}

/**
 * Options for configuring the DnD container controller
 */
export interface DnDContainerOptions<T extends DraggedItem> {
  onReorder: (items: T[]) => void;
  onDropComplete?: (item: T, newPosition: number) => void;
  getValidDropPositions: (sourceItem: T) => ("top" | "right" | "bottom" | "left")[];
}

/**
 * Generic drag and drop container controller that manages drop zones and reordering
 * Works with any data type that has an id property
 */
export class DnDContainerController<T extends DraggedItem> implements ReactiveController {
  private draggedItem: T | null = null;
  private dropPosition: DropPosition | null = null;
  private containerElement: HTMLElement | null = null;
  private abortController = new AbortController();

  constructor(
    private host: ReactiveControllerHost,
    private containerRef: Ref<HTMLElement>,
    private options: DnDContainerOptions<T>,
  ) {
    host.addController(this);
  }

  get getDraggedItem(): T | null {
    return this.draggedItem;
  }

  hostConnected() {
    this.setupEventListeners();
  }

  hostDisconnected() {
    this.cleanup();
  }

  hostUpdated() {
    // Update container element reference if it changed
    const element = this.containerRef.value || null;
    if (this.containerElement !== element) {
      this.containerElement = element;
      this.setupEventListeners();
    }
  }

  private setupEventListeners() {
    if (!this.containerElement) return;

    // Clean up previous listeners
    this.abortController.abort();
    this.abortController = new AbortController();

    const signal = this.abortController.signal;

    // Listen for drag events from child items
    this.containerElement.addEventListener("item-drag-start", this.handleItemDragStart as EventListener, { signal });
    this.containerElement.addEventListener("item-drag-end", this.handleItemDragEnd as EventListener, { signal });
    this.containerElement.addEventListener("item-drag-over", this.handleItemDragOver as EventListener, { signal });
    this.containerElement.addEventListener("item-drag-leave", this.handleItemDragLeave as EventListener, { signal });
    this.containerElement.addEventListener(
      "get-valid-drop-positions",
      this.handleGetValidDropPositions as EventListener,
      { signal },
    );

    // Handle custom drop events from pointer-based dragging
    this.containerElement.addEventListener("custom-drop", this.handleCustomDrop as EventListener, { signal });
  }

  private handleItemDragStart = (event: CustomEvent) => {
    const { item } = event.detail;
    this.draggedItem = item;

    this.host.requestUpdate();
  };

  private handleItemDragEnd = () => {
    this.endDrag();
  };

  private handleItemDragOver = (event: CustomEvent) => {
    const { itemId, side, targetElement } = event.detail;
    this.setDropPosition(itemId, side, targetElement);
  };

  private handleItemDragLeave = () => {
    this.clearDropPosition();
  };

  private handleGetValidDropPositions = (event: CustomEvent) => {
    // Provide valid drop positions for the requesting item controller
    const { callback } = event.detail;
    if (callback && this.draggedItem) {
      const validPositions = this.options.getValidDropPositions(this.draggedItem);
      callback(validPositions);
    }
  };

  private handleCustomDrop = (event: CustomEvent) => {
    event.preventDefault();

    // Emit event to request host to handle the drop with current items
    const dropEvent = new CustomEvent("dnd-drop-requested", {
      bubbles: true,
      composed: true,
      detail: { originalEvent: event },
    });

    if (this.containerElement) {
      this.containerElement.dispatchEvent(dropEvent);
    }
  };

  private setDropPosition(targetItemId: string, side: "left" | "right" | "top" | "bottom", targetElement: HTMLElement) {
    if (!this.draggedItem) return;

    // Don't allow dropping on the dragged item itself
    if (this.draggedItem.id === targetItemId) return;

    this.dropPosition = {
      targetItemId,
      side,
      targetElement,
    };
    this.host.requestUpdate();
  }

  private clearDropPosition() {
    this.dropPosition = null;
    this.host.requestUpdate();
  }

  private endDrag() {
    this.draggedItem = null;
    this.dropPosition = null;
    this.host.requestUpdate();
  }

  private calculateNewOrder(allItems: T[], draggedItem: T, dropPosition: DropPosition): T[] {
    const filteredItems = allItems.filter((item) => item.id !== draggedItem.id);
    const targetIndex = filteredItems.findIndex((item) => item.id === dropPosition.targetItemId);

    if (targetIndex === -1) {
      return [...filteredItems, draggedItem];
    }

    // Determine insert position based on drop side
    const insertIndex = ["right", "bottom"].includes(dropPosition.side) ? targetIndex + 1 : targetIndex;

    const newOrder = filteredItems.toSpliced(insertIndex, 0, draggedItem);

    return newOrder;
  }

  // Method for host component to provide current items during drop
  processDropWithItems(currentItems: T[]): void {
    if (!this.draggedItem || !this.dropPosition) {
      this.endDrag();
      return;
    }

    const newOrder = this.calculateNewOrder(currentItems, this.draggedItem, this.dropPosition);
    this.options.onReorder(newOrder);

    // Call completion callback if provided
    this.options.onDropComplete?.(this.draggedItem, newOrder.length - 1);

    this.endDrag();
  }

  private cleanup() {
    this.abortController.abort();
    this.endDrag();
  }

  // Public getters for template usage
  get isDragging() {
    return this.draggedItem !== null;
  }

  get currentDropPosition() {
    return this.dropPosition;
  }

  // Get valid drop positions for current drag operation
  getValidDropPositions(): ("top" | "right" | "bottom" | "left")[] {
    if (!this.draggedItem) return [];
    return this.options.getValidDropPositions(this.draggedItem);
  }
}
